import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError } from "../api/client";
import type { ItemRights } from "../api/types";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Presence } from "../components/presence";
import { Badge, Segmented } from "../components/ui";
import { t } from "../i18n/i18n";
import { useUi } from "../state/ui";
import { accountsItem, itemOwned, itemPrivate } from "./access";

/** Visibilité d'un ordre de bataille construit ou d'un terrain libre (docs/17 §3.6, §8.3, lot 5) : « Visible par
 *  toute l'unité » (défaut, bibliothèque commune) ou « Privé ». Réglée par son propriétaire et les administrateurs ;
 *  les autres comptes voient seulement l'auteur. Passer en « Privé » n'ôte rien aux dossiers qui l'emploient déjà : la
 *  confirmation les compte. Un élément de l'unité sans propriétaire reste visible par tous : pastille « Unité »
 *  seulement. Rien en mode hérité (l'appelant ne le monte pas). */
export function VisibilityControl<T extends ItemRights>({
  item,
  users,
  onChange,
  onSaved,
}: {
  item: T;
  /** Dossiers qui l'emploient déjà. */
  users: number;
  onChange: (visibility: "unit" | "private") => Promise<T>;
  onSaved: (next: T) => void;
}) {
  const notify = useUi((s) => s.notify);
  const [confirming, setConfirming] = useState(false);
  const save = useMutation({
    mutationFn: onChange,
    onSuccess: (next, visibility) => {
      setConfirming(false);
      onSaved(next);
      notify({ text: visibility === "private" ? t("Élément privé") : t("Élément visible par toute l'unité"), tone: "ok" });
    },
    onError: (e: Error) => {
      setConfirming(false);
      notify({ text: t("Visibilité inchangée"), detail: e instanceof ApiError ? e.message : undefined, tone: "error" });
    },
  });
  if (accountsItem(item) && !item.owner) {
    // Élément de l'unité sans propriétaire : visible par toute l'unité tant qu'un administrateur ne lui en a pas
    // attribué un (« Privé » n'y cacherait rien, le serveur le refuse).
    return <Badge>{t("Unité")}</Badge>;
  }
  if (!itemOwned(item)) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5">
        {itemPrivate(item) && <Badge>{t("Privé")}</Badge>}
        {item.owner && <span className="text-meta text-ink-muted">{t("par {nom}", { nom: item.owner.name })}</span>}
      </span>
    );
  }
  const value = itemPrivate(item) ? "private" : "unit";
  return (
    <>
      <Segmented
        size="sm"
        fill={false}
        ariaLabel={t("Visibilité")}
        value={value}
        onChange={(next) => {
          if (next === value || save.isPending) return;
          if (next === "private" && users > 0) setConfirming(true);
          else save.mutate(next);
        }}
        options={[
          { value: "unit", label: t("Visible par toute l'unité") },
          { value: "private", label: t("Privé") },
        ]}
      />
      <Presence show={confirming}>
        {confirming && (
          <ConfirmDialog
            title={t("Rendre privé ?")}
            confirmLabel={t("Rendre privé")}
            tone="neutral"
            busy={save.isPending}
            onConfirm={() => save.mutate("private")}
            onCancel={() => setConfirming(false)}
          >
            {t("{n} dossiers l'emploient déjà : ils continuent de le lire, sans le montrer dans les listes.", { n: users })}
          </ConfirmDialog>
        )}
      </Presence>
    </>
  );
}
